import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { assessmentService } from '../services/assessmentService';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, FileText, Calendar, TrendingUp, Download, User, LogOut, X, Lock, Eye, EyeOff, Mail, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import api from '../services/api';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const { language, setLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [completions, setCompletions] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('name');
  const menuRef = useRef(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const texts = {
    en: {
      welcome: 'Welcome',
      importantReminder: 'Important Reminder',
      reminderText: 'This questionnaire is uniquely intended to act as a guideline. It is not a psychometric tool, nor is it scientifically-based and researched. It was developed using many sources of information and resources.',
      retakeBtn: 'Retake Well-Being Self-Assessment',
      historyTitle: 'Your Self-Assessment History',
      noHistory: 'No self-assessments completed yet',
      takeFirst: 'Take Your First Self-Assessment',
      downloadReport: 'Download Report',
      userDetails: 'User Details',
      signOut: 'Sign Out',
      changeName: 'Change Name',
      changeEmail: 'Change Email',
      changePassword: 'Change Password',
      firstName: 'First Name',
      lastName: 'Last Name',
      emailConfirmNote: 'A confirmation email will be sent to your current email address.',
      newEmail: 'New Email Address',
      requestEmailChange: 'Request Email Change',
      currentPassword: 'Current Password',
      newPasswordLabel: 'New Password',
      confirmPasswordLabel: 'Confirm New Password',
      atLeast8: 'At least 8 characters',
      reenter: 'Re-enter new password',
      updateName: 'Update Name',
      changePasswordBtn: 'Change Password',
      cancel: 'Cancel',
      saving: 'Saving...',
      sending: 'Sending...',
      changing: 'Changing...',
    },
    fr: {
      welcome: 'Bienvenue',
      importantReminder: 'Rappel important',
      reminderText: 'Cette auto-évaluation est uniquement destinée à servir de guide. Il ne s\'agit pas d\'un outil psychométrique, ni d\'un outil basé sur la science et la recherche. Elle a été élaborée en utilisant de nombreuses sources d\'information et de ressources.',
      retakeBtn: 'Reprendre l\'auto-évaluation du bien-être',
      historyTitle: 'Votre historique d\'auto-évaluation',
      noHistory: 'Aucune auto-évaluation complétée',
      takeFirst: 'Faire votre première auto-évaluation',
      downloadReport: 'Télécharger le rapport',
      userDetails: 'Détails de l\'utilisateur',
      signOut: 'Se déconnecter',
      changeName: 'Changer le nom',
      changeEmail: 'Changer le courriel',
      changePassword: 'Changer le mot de passe',
      firstName: 'Prénom',
      lastName: 'Nom',
      emailConfirmNote: 'Un courriel de confirmation sera envoyé à votre adresse actuelle.',
      newEmail: 'Nouvelle adresse courriel',
      requestEmailChange: 'Demander le changement',
      currentPassword: 'Mot de passe actuel',
      newPasswordLabel: 'Nouveau mot de passe',
      confirmPasswordLabel: 'Confirmer le nouveau mot de passe',
      atLeast8: 'Au moins 8 caractères',
      reenter: 'Ressaisir le nouveau mot de passe',
      updateName: 'Mettre à jour le nom',
      changePasswordBtn: 'Changer le mot de passe',
      cancel: 'Annuler',
      saving: 'Enregistrement...',
      sending: 'Envoi...',
      changing: 'Changement...',
    }
  };

  const txt = texts[language] || texts.en;

  const loadCompletions = useCallback(async () => {
    try {
      const data = await assessmentService.getUserCompletions();
      setCompletions(data);
    } catch (error) {
      toast.error(language === 'fr' ? 'Échec du chargement' : 'Failed to load completions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    setFirstName(user.first_name || '');
    setLastName(user.last_name || '');
    setEmail(user.email || '');
    loadCompletions();
  }, [user, navigate, loadCompletions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success(language === 'fr' ? 'Déconnexion réussie' : 'Logged out successfully');
  };

  const getUserInitials = () => {
    const first = user?.first_name?.charAt(0)?.toUpperCase() || '';
    const last = user?.last_name?.charAt(0)?.toUpperCase() || '';
    return first + last || 'U';
  };

  const handleDownloadReport = (reportUrl) => {
    const link = document.createElement('a');
    link.href = reportUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpdateProfile = async () => {
    const newErrors = {};
    
    if (!firstName.trim()) {
      newErrors.firstName = language === 'fr' ? 'Prénom requis' : 'First name is required';
    }
    if (!lastName.trim()) {
      newErrors.lastName = language === 'fr' ? 'Nom requis' : 'Last name is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      await api.put('/auth/update-profile', {
        first_name: firstName.trim(),
        last_name: lastName.trim()
      });
      toast.success(language === 'fr' ? 'Profil mis à jour' : 'Profile updated successfully');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Échec de la mise à jour' : 'Failed to update profile'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!email.trim() || email === user.email) {
      toast.error(language === 'fr' ? 'Veuillez entrer une nouvelle adresse' : 'Please enter a new email address');
      return;
    }

    setSaving(true);
    try {
      await api.post('/auth/request-email-change', {
        new_email: email.trim()
      });
      toast.success(language === 'fr' ? 'Courriel de confirmation envoyé' : 'Confirmation email sent to your current email address.');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Échec de la demande' : 'Failed to request email change'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const newErrors = {};
    
    if (!currentPassword) {
      newErrors.currentPassword = language === 'fr' ? 'Mot de passe actuel requis' : 'Current password is required';
    }
    if (!newPassword) {
      newErrors.newPassword = language === 'fr' ? 'Nouveau mot de passe requis' : 'New password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = language === 'fr' ? 'Au moins 8 caractères' : 'Password must be at least 8 characters';
    }
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      await api.put('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success(language === 'fr' ? 'Mot de passe changé' : 'Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowUserDetails(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Échec du changement' : 'Failed to change password'));
    } finally {
      setSaving(false);
    }
  };

  const closeUserDetails = () => {
    setShowUserDetails(false);
    setErrors({});
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setActiveTab('name');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-brand-50/20 to-white">
      <header className="border-b border-brand-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/spc-logo.png" alt="SPC Logo" className="w-12 h-12 object-contain rounded-lg" />
              <div>
                <h1 className="text-xl font-black text-gray-800" style={{ fontFamily: 'Avenir, sans-serif' }}>
                  {language === 'fr' ? 'AUTO-ÉVALUATION DU BIEN-ÊTRE' : 'WELL-BEING SELF-ASSESSMENT'}
                </h1>
                <p className="text-xs text-gray-600">{language === 'fr' ? 'Centre des points forts' : 'Strengths Performance Centre'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1.5 text-sm font-semibold transition-colors ${
                    language === 'en' 
                      ? 'bg-brand-600 text-white' 
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="lang-en-btn"
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('fr')}
                  className={`px-3 py-1.5 text-sm font-semibold transition-colors ${
                    language === 'fr' 
                      ? 'bg-brand-600 text-white' 
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="lang-fr-btn"
                >
                  FR
                </button>
              </div>

              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="w-10 h-10 rounded-full bg-brand-600 flex items-center justify-center text-white font-bold hover:bg-brand-700 transition-colors"
                  data-testid="user-menu-btn"
                >
                  {getUserInitials()}
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        setShowUserDetails(true);
                      }}
                      className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      data-testid="user-details-btn"
                    >
                      <User className="h-4 w-4" />
                      {txt.userDetails}
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      data-testid="sign-out-btn"
                    >
                      <LogOut className="h-4 w-4" />
                      {txt.signOut}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-2xl font-black text-gray-900 mb-6">
          {txt.welcome}, {user.first_name}!
        </h2>

        <div className="bg-brand-50 border-l-4 border-brand-600 p-4 mb-8 rounded-r-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-gray-900 mb-1">{txt.importantReminder}</p>
              <p className="text-sm text-gray-700 leading-relaxed">
                {txt.reminderText}
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
          </div>
        ) : (
          <div className="space-y-8">
            <Card className="shadow-lg border-brand-200">
              <CardContent className="pt-6">
                <Button
                  onClick={() => navigate('/assessment/start')}
                  size="lg"
                  className="w-full bg-brand-600 hover:bg-brand-700 py-6"
                  data-testid="retake-assessment-btn"
                >
                  <TrendingUp className="mr-2 h-5 w-5" />
                  {txt.retakeBtn}
                </Button>
              </CardContent>
            </Card>

            <div>
              <h2 className="text-2xl font-black text-gray-900 mb-4">
                {txt.historyTitle}
              </h2>
              
              {completions.length === 0 ? (
                <Card className="shadow-md">
                  <CardContent className="pt-6 text-center py-12">
                    <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">{txt.noHistory}</p>
                    <Button onClick={() => navigate('/assessment/start')}>
                      {txt.takeFirst}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {completions.map((completion) => {
                    const result = completion.results;
                    return (
                      <Card key={completion.id} className="shadow-sm hover:shadow-md transition-shadow">
                        <CardContent className="py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-gray-900">
                                {language === 'fr' ? 'Questionnaire de bien-être' : 'Well-Being Self-Assessment'}
                              </h3>
                              <div className="flex items-center gap-2 text-gray-500 text-sm mt-1">
                                <Calendar className="h-4 w-4" />
                                {format(new Date(completion.completed_at), 'MMMM d, yyyy')}
                              </div>
                            </div>
                            
                            {result && (
                              <div className="flex-1 text-center">
                                <div className="text-2xl font-black text-brand-600">
                                  {result.overall_label}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {result.overall_action_phrase}
                                </div>
                              </div>
                            )}
                            
                            {result && (
                              <div className="flex-shrink-0">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownloadReport(result.report_url)}
                                  data-testid="download-report-btn"
                                >
                                  <Download className="mr-2 h-4 w-4" />
                                  {txt.downloadReport}
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showUserDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm shadow-2xl">
            <div className="p-5">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-black text-gray-900">{txt.userDetails}</h2>
                <button 
                  onClick={closeUserDetails}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex border-b border-gray-200 mb-4">
                <button
                  onClick={() => setActiveTab('name')}
                  className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'name' 
                      ? 'border-brand-600 text-brand-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {language === 'fr' ? 'Nom' : 'Name'}
                </button>
                <button
                  onClick={() => setActiveTab('email')}
                  className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'email' 
                      ? 'border-brand-600 text-brand-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {language === 'fr' ? 'Courriel' : 'Email'}
                </button>
                <button
                  onClick={() => setActiveTab('password')}
                  className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'password' 
                      ? 'border-brand-600 text-brand-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {language === 'fr' ? 'Mot de passe' : 'Password'}
                </button>
              </div>

              {activeTab === 'name' && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="firstName" className="text-sm">{txt.firstName}</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        if (errors.firstName) setErrors(prev => ({ ...prev, firstName: null }));
                      }}
                      className={`mt-1 ${errors.firstName ? 'border-red-500' : ''}`}
                    />
                    {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-sm">{txt.lastName}</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        if (errors.lastName) setErrors(prev => ({ ...prev, lastName: null }));
                      }}
                      className={`mt-1 ${errors.lastName ? 'border-red-500' : ''}`}
                    />
                    {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={closeUserDetails} className="flex-1" size="sm">
                      {txt.cancel}
                    </Button>
                    <Button onClick={handleUpdateProfile} disabled={saving} className="flex-1" size="sm">
                      {saving ? txt.saving : txt.updateName}
                    </Button>
                  </div>
                </div>
              )}

              {activeTab === 'email' && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500">{txt.emailConfirmNote}</p>
                  <div>
                    <Label htmlFor="email" className="text-sm">{txt.newEmail}</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={closeUserDetails} className="flex-1" size="sm">
                      {txt.cancel}
                    </Button>
                    <Button onClick={handleChangeEmail} disabled={saving || email === user.email} className="flex-1" size="sm">
                      {saving ? txt.sending : txt.requestEmailChange}
                    </Button>
                  </div>
                </div>
              )}

              {activeTab === 'password' && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="currentPassword" className="text-sm">{txt.currentPassword}</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        id="currentPassword"
                        type={showCurrentPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => {
                          setCurrentPassword(e.target.value);
                          if (errors.currentPassword) setErrors(prev => ({ ...prev, currentPassword: null }));
                        }}
                        className={`pl-9 pr-9 ${errors.currentPassword ? 'border-red-500' : ''}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.currentPassword && <p className="text-red-500 text-xs mt-1">{errors.currentPassword}</p>}
                  </div>

                  <div>
                    <Label htmlFor="newPassword" className="text-sm">{txt.newPasswordLabel}</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          if (errors.newPassword) setErrors(prev => ({ ...prev, newPassword: null }));
                        }}
                        className={`pl-9 pr-9 ${errors.newPassword ? 'border-red-500' : ''}`}
                        placeholder={txt.atLeast8}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.newPassword && <p className="text-red-500 text-xs mt-1">{errors.newPassword}</p>}
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword" className="text-sm">{txt.confirmPasswordLabel}</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: null }));
                        }}
                        className={`pl-9 pr-9 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                        placeholder={txt.reenter}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={closeUserDetails} className="flex-1" size="sm">
                      {txt.cancel}
                    </Button>
                    <Button onClick={handleChangePassword} disabled={saving} className="flex-1" size="sm">
                      {saving ? txt.changing : txt.changePasswordBtn}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
